// Help Hub guide registry (in-code). Add a feature by importing its guide here.
// Guides are locale-keyed: `content.<lang>` holds { feature, title, sections }.
import timelineGuide from "./timeline";
import rightMenuGuide from "./rightMenu";
import imageToolsGuide from "./imageTools";
import scenesGuide from "./scenes";
import sceneSettingsGuide from "./sceneSettings";
import canvasBasicsGuide from "./canvasBasics";
import keyboardShortcutsGuide from "./keyboardShortcuts";
import componentsGuide from "./components";
import tablesChartsGuide from "./tablesCharts";
import interactiveScenariosGuide from "./interactiveScenarios";
import addImageGuide from "./addImage";
import addVideoGuide from "./addVideo";
import narrationAvatarGuide from "./narrationAvatar";
import infographicSceneGuide from "./infographicScene";
import syncWithAiGuide from "./syncWithAi";
import assistantGuide from "./assistant";
import aiWizardGuide from "./aiWizard";
import jobTrayGuide from "./jobTray";
import assessmentsGuide from "./assessments";
import libraryGuide from "./library";
import brandKitGuide from "./brandKit";
import screenTrainingGuide from "./screenTraining";
import knowledgeBaseGuide from "./knowledgeBase";
import exportGuide from "./export";
import videoExportGuide from "./videoExport";
import subtitlesGuide from "./subtitles";
import courseSettingsGuide from "./courseSettings";
import creditsActivityGuide from "./creditsActivity";

// Order matters: the rail groups guides by `feature` in first-seen order.
export const GUIDES = [
  timelineGuide,
  rightMenuGuide,
  imageToolsGuide,
  scenesGuide,
  sceneSettingsGuide,
  canvasBasicsGuide,
  keyboardShortcutsGuide,
  componentsGuide,
  tablesChartsGuide,
  interactiveScenariosGuide,
  addImageGuide,
  addVideoGuide,
  narrationAvatarGuide,
  infographicSceneGuide,
  syncWithAiGuide,
  assistantGuide,
  aiWizardGuide,
  jobTrayGuide,
  assessmentsGuide,
  libraryGuide,
  brandKitGuide,
  screenTrainingGuide,
  knowledgeBaseGuide,
  exportGuide,
  videoExportGuide,
  subtitlesGuide,
  courseSettingsGuide,
  creditsActivityGuide,
];

const DEFAULT_LANG = "en";

// Flatten a locale-keyed guide to a single-language shape the UI/search use:
// { id, icon, keywords, feature, title, sections }. Falls back to English when
// the requested language isn't authored.
export function resolveGuide(guide, lang = DEFAULT_LANG) {
  const c = (guide.content && (guide.content[lang] || guide.content[DEFAULT_LANG])) || {};
  return {
    id: guide.id,
    icon: guide.icon,
    keywords: guide.keywords || [],
    feature: c.feature,
    title: c.title,
    sections: c.sections || [],
  };
}

export function resolveGuides(lang = DEFAULT_LANG, guides = GUIDES) {
  return guides.map((g) => resolveGuide(g, lang));
}

// Group resolved guides by their `feature` label, preserving first-seen order.
export function groupByFeature(guides) {
  const order = [];
  const byFeature = new Map();
  for (const g of guides) {
    if (!byFeature.has(g.feature)) {
      byFeature.set(g.feature, []);
      order.push(g.feature);
    }
    byFeature.get(g.feature).push(g);
  }
  return order.map((feature) => ({ feature, guides: byFeature.get(feature) }));
}

// Global help search over RESOLVED guides. Empty query → all. Otherwise
// case-insensitive substring over title, keywords, and section title/text.
// `matchedSectionIds` lists the sections that hit; title/keyword hits rank
// above section-only hits.
export function searchGuides(query, guides = resolveGuides()) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return guides.map((g) => ({ ...g, matchedSectionIds: [] }));

  const scored = [];
  for (const g of guides) {
    const titleHit = (g.title || "").toLowerCase().includes(q);
    const keywordHit = (g.keywords || []).some((k) => k.toLowerCase().includes(q));
    const matchedSectionIds = (g.sections || [])
      .filter((s) => s.title.toLowerCase().includes(q) || (s.text || "").toLowerCase().includes(q))
      .map((s) => s.id);

    if (titleHit || keywordHit || matchedSectionIds.length) {
      // 0 = strongest (title/keyword), 1 = section-only.
      const rank = titleHit || keywordHit ? 0 : 1;
      scored.push({ guide: { ...g, matchedSectionIds }, rank });
    }
  }
  scored.sort((a, b) => a.rank - b.rank);
  return scored.map((s) => s.guide);
}
